/**
 * Quarantine Spike Card (MA-UI-107)
 * 
 * Executive alert module for abnormal quarantine behavior.
 * Shows operational signal that may need intervention.
 */

import React from 'react';
import { Shield, AlertTriangle, CheckCircle, Info, ChevronRight, Activity } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Link } from 'react-router-dom';

// =============================================================================
// STATE CONFIG
// =============================================================================

const STATE_CONFIG = {
  normal: {
    title: 'No spike detected',
    subtitle: 'Quarantine activity is within normal range.',
    icon: CheckCircle,
    iconClass: 'text-emerald-400',
    borderColor: 'border-l-emerald-500/50',
    bgClass: 'bg-emerald-500/5',
    badgeClass: 'bg-emerald-500/10 text-emerald-400',
  },
  watch: {
    title: 'Quarantine activity elevated',
    subtitle: 'Above baseline but not yet critical.',
    icon: Activity,
    iconClass: 'text-amber-400',
    borderColor: 'border-l-amber-500/50',
    bgClass: 'bg-amber-500/5',
    badgeClass: 'bg-amber-500/10 text-amber-400',
  },
  critical: {
    title: 'Quarantine spike detected',
    subtitle: 'Significant deviation from baseline detected.',
    icon: AlertTriangle,
    iconClass: 'text-red-400',
    borderColor: 'border-l-red-500/50',
    bgClass: 'bg-red-500/5',
    badgeClass: 'bg-red-500/10 text-red-400 animate-pulse',
  },
};

// =============================================================================
// QUARANTINE SPIKE CARD
// =============================================================================

export function QuarantineSpikeCard({ data, isLoading }) {
  // Loading state
  if (isLoading) {
    return (
      <Card className="glass border-white/10 border-l-4 border-l-slate-500/50">
        <CardContent className="p-5">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-slate-700 rounded w-1/2"></div>
            <div className="h-6 bg-slate-700 rounded w-3/4"></div>
            <div className="h-3 bg-slate-700 rounded w-full"></div>
            <div className="h-8 bg-slate-700 rounded w-1/3"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Empty/default state
  if (!data) {
    return (
      <Card className="glass border-white/10 border-l-4 border-l-slate-500/50">
        <CardContent className="p-5">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-300">Quarantine Alert</span>
            </div>
            <p className="text-sm text-slate-400">No spike data available</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { state, recent_count, baseline_count, multiplier, top_funnel } = data;
  const config = STATE_CONFIG[state] || STATE_CONFIG.normal;
  const StateIcon = config.icon;

  return (
    <Card 
      className={`glass border-white/10 border-l-4 ${config.borderColor} ${config.bgClass}`}
      data-testid="quarantine-spike-card"
    >
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-lg ${state === 'critical' ? 'bg-red-500/10' : state === 'watch' ? 'bg-amber-500/10' : 'bg-emerald-500/10'}`}>
              <Shield className={`h-4 w-4 ${config.iconClass}`} />
            </div>
            <span className="text-sm font-medium text-slate-300">Quarantine Alert</span>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-3.5 w-3.5 text-slate-500" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <p className="text-xs">
                  Compares recent quarantine activity against the trailing 7-day hourly baseline 
                  to detect operational anomalies.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* State-driven headline */}
        <div className="flex items-center gap-2 mb-2">
          <StateIcon className={`h-5 w-5 ${config.iconClass}`} />
          <span className="text-base font-semibold text-slate-100">{config.title}</span>
        </div>

        {/* Subtitle */}
        <p className="text-xs text-slate-400 mb-4">
          {config.subtitle}
        </p>

        {/* Details row */}
        <div className="grid grid-cols-3 gap-2 text-xs mb-4">
          <div className="bg-slate-800/50 rounded-lg p-2 text-center">
            <div className="text-slate-500 mb-0.5">Last 60m</div>
            <div className={`font-bold text-lg ${state === 'critical' ? 'text-red-400' : state === 'watch' ? 'text-amber-400' : 'text-slate-200'}`}>
              {recent_count}
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-2 text-center">
            <div className="text-slate-500 mb-0.5">Baseline</div>
            <div className="font-medium text-slate-300">
              {baseline_count?.toFixed(1) || '—'}
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-2 text-center">
            <div className="text-slate-500 mb-0.5">Multiple</div>
            <div className={`font-medium ${multiplier && multiplier >= 2 ? 'text-red-400' : multiplier && multiplier >= 1.5 ? 'text-amber-400' : 'text-slate-300'}`}>
              {multiplier ? `${multiplier}x` : '—'}
            </div>
          </div>
        </div>

        {/* Top funnel if available */}
        {top_funnel && state !== 'normal' && (
          <div className="text-xs text-slate-400 mb-3">
            <span className="text-slate-500">Top Funnel:</span>{' '}
            <span className="text-slate-300">{top_funnel}</span>
          </div>
        )}

        {/* CTA - View Quarantined Leads */}
        <Link to="/admin/diagnostics?filter=quarantine">
          <Button
            variant="outline"
            size="sm"
            className={`w-full text-xs ${
              state === 'critical' 
                ? 'border-red-500/30 text-red-300 hover:bg-red-500/10' 
                : state === 'watch'
                ? 'border-amber-500/30 text-amber-300 hover:bg-amber-500/10'
                : 'border-slate-600 text-slate-300 hover:bg-slate-700/50'
            }`}
          >
            View Quarantined Leads
            <ChevronRight className="h-3 w-3 ml-1" />
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}

export default QuarantineSpikeCard;
